import { Line } from 'react-chartjs-2';

import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Filler,
  Legend,
  Plugin,
  TooltipItem,
  Scale,
  Chart,
} from 'chart.js';
import { EmptyLineOrBarChart } from './EmptyLineOrBarChart';
import { useTranslation } from 'react-i18next';
import { toLocaleBrowserSettings } from 'src/i18n/numberLocale';
import Box from '@mui/material/Box';
import { getDefaultAreaChartXaxisLabels } from 'src/common/components/Graphs/common/Graphs/utils/getDefaultAreaChartXAxisLabels';

const POINT_RADIUS = 3;
const LINE_WIDTH = 1;

const BACKGROUND_RED_COLOR = '#fabec2';
const BEFORE_PROJECTED_RED_AREA_BACKGROUND_COLOR = '#f4a2a7';

const LIMIT_GOLD_COLOR = '#e9a624';
const LIMIT_LINE_WIDTH = 2;

const overLimitAreaPlugin: Plugin<'line'> = {
  id: 'overLimitAreaPlugin',
  afterDatasetsDraw(chart, _, options) {
    const {
      ctx,
      scales: { y },
    } = chart;

    const limit = options.limit;
    const clipUntil = options.clipUntil;
    const mode = options.mode || 'full';

    if (!limit) return;

    const meta = chart.getDatasetMeta(0);
    const pts = meta.data as PointElement[];
    const values = chart.data.datasets[0].data.map(Number);
    const maxIndex = pts.length - 1;
    const yLimit = y.getPixelForValue(limit);

    const drawSegment = (
      p0: PointElement,
      p1: PointElement,
      index0: number,
      index1: number,
      color: string,
    ) => {
      ctx.fillStyle = color;
      const v0 = values[index0];
      const v1 = values[index1];

      const bothAbove = v0 >= limit && v1 >= limit;
      const crosses = (v0 < limit && v1 > limit) || (v0 > limit && v1 < limit);

      if (bothAbove) {
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p0.x, yLimit);
        ctx.lineTo(p1.x, yLimit);
        ctx.lineTo(p1.x, p1.y);
        ctx.closePath();
        ctx.fill();
      } else if (crosses) {
        const t = (limit - v0) / (v1 - v0);
        const xi = p0.x + t * (p1.x - p0.x);
        const yi = p0.y + t * (p1.y - p0.y);

        if (v0 > limit) {
          ctx.beginPath();
          ctx.moveTo(p0.x, p0.y);
          ctx.lineTo(p0.x, yLimit);
          ctx.lineTo(xi, yLimit);
          ctx.lineTo(xi, yi);
          ctx.closePath();
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.moveTo(xi, yi);
          ctx.lineTo(xi, yLimit);
          ctx.lineTo(p1.x, yLimit);
          ctx.lineTo(p1.x, p1.y);
          ctx.closePath();
          ctx.fill();
        }
      }
    };

    ctx.save();

    for (let i = 0; i < maxIndex; i++) {
      const p0 = pts[i];
      const p1 = pts[i + 1];

      if (mode === 'full') {
        drawSegment(p0, p1, i, i + 1, options.overlimitBackgroundColor);
      } else if (mode === 'clipped' && clipUntil != null && i < clipUntil) {
        drawSegment(
          p0,
          p1,
          i,
          i + 1,
          options.clippedBackgroundColor ?? options.overlimitBackgroundColor,
        );
      } else if (mode === 'both') {
        const isClipped = clipUntil != null && i < clipUntil;
        const color = isClipped
          ? options.clippedBackgroundColor ?? options.overlimitBackgroundColor
          : options.overlimitBackgroundColor;
        drawSegment(p0, p1, i, i + 1, color);
      }
    }

    ctx.restore();
  },
};

const gridOnTopPlugin: Plugin<'line'> = {
  id: 'gridOnTop',
  afterDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    const xAxis = scales.x;
    const yAxis = scales.y;

    ctx.save();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 1;

    // Horizontal lines
    yAxis.getTicks().forEach((tick) => {
      const y = yAxis.getPixelForValue(tick.value);
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
    });

    // Vertical lines
    xAxis.getTicks().forEach((tick) => {
      const x = xAxis.getPixelForValue(tick.value);
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
    });

    ctx.restore();
  },
};

const customPointsPlugin: Plugin<'line'> = {
  id: 'customPointsPlugin',
  afterDatasetsDraw(chart, _, pluginOptions: { pointRadius: number }) {
    const datasetIndex = 0; // Main dataset index (lines + no points)
    const meta = chart.getDatasetMeta(datasetIndex);
    const points = meta.data;
    const dataset: any = chart.data.datasets[datasetIndex];

    if (!points || !dataset?.pointBackgroundColor) return;

    const ctx = chart.ctx;

    ctx.save();

    points.forEach((point, i) => {
      const radius = points.length === 1 ? POINT_RADIUS : pluginOptions.pointRadius;
      let bgColor = '#000';

      const pointBgColor = dataset.pointBackgroundColor;
      if (Array.isArray(pointBgColor)) {
        bgColor = pointBgColor[i] || bgColor;
      } else if (typeof pointBgColor === 'string') {
        bgColor = pointBgColor;
      }

      ctx.beginPath();
      ctx.fillStyle = bgColor;
      ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI);
      ctx.fill();
    });

    ctx.restore();
  },
};

export type DatasetPoint = {
  label: string;
  value: string;
  backgroundColor: string;
  borderColor: string;
  pointColor?: string;
  solidLine?: boolean;
};

type DataSetItem = {
  label: string;
  data: DatasetPoint[];
  displayBorder: boolean;
  order: number;
  fill?: boolean;
};

type AreaChartProps = {
  datasets: DataSetItem[];
  limit?: number;
  yAxisLabel?: string;
  xAxisLabels?: string[];
  limitLabel?: string;
  limitColor?: string;
  realDataEndIndex?: number;
  pointRadius?: number;
  yAxisStepSize?: number;
  tooltipLabelPriority?: Record<string, number>;
  tooltipValueFormatter?: (value: number, unit?: string) => string;
};

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Filler, Legend);

const defaultXaxisLabels = getDefaultAreaChartXaxisLabels();

export const AreaChart = ({
  datasets,
  limit,
  yAxisLabel,
  xAxisLabels = defaultXaxisLabels,
  limitLabel = 'Limit',
  limitColor = LIMIT_GOLD_COLOR,
  realDataEndIndex,
  pointRadius = POINT_RADIUS,
  yAxisStepSize,
  tooltipLabelPriority,
  tooltipValueFormatter,
}: AreaChartProps) => {
  const { t } = useTranslation();

  const data = {
    labels: xAxisLabels,
    datasets: [
      ...datasets.map((set) => ({
        label: set.label,
        data: set.data.map((dataPoint) => dataPoint.value),
        fill: set.fill,
        pointBackgroundColor: set.data.map((dataPoint) => dataPoint.pointColor),
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        pointBorderWidth: 0,
        borderWidth: set.displayBorder ? LINE_WIDTH : 0,
        order: set.order,
        segment: {
          backgroundColor: (ctx: { p0DataIndex: number }) => {
            const i = ctx.p0DataIndex;
            const point = set.data[i];

            return point?.backgroundColor;
          },
          borderColor: (ctx: { p0DataIndex: number }) => {
            const i = ctx.p0DataIndex;
            const point = set.data[i];
            return point?.borderColor;
          },
          borderDash: (ctx: { p0DataIndex: number }) => {
            const i = ctx.p0DataIndex;
            const point = set.data[i];
            return point?.solidLine ? [] : [3, 3];
          },
        },
      })),
      ...(limit
        ? [
            {
              label: limitLabel,
              data: new Array(xAxisLabels.length).fill(limit),
              borderColor: limitColor,
              backgroundColor: limitColor,
              pointRadius: 0,
              pointHoverRadius: 0,
              borderWidth: LIMIT_LINE_WIDTH,
              order: 0,
            },
          ]
        : []),
    ],
  };

  const options: Chart<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: yAxisStepSize,
          precision: 0,
          callback: (value: number | string) => {
            return `${toLocaleBrowserSettings(Number.parseFloat(value.toString()))} ${yAxisLabel}`;
          },
          color: ({ tick }: { tick: { value: number | string } }) => {
            return limit && tick.value === limit ? limitColor : '#707676';
          },
        },
      },
      x: {
        ticks: {
          minRotation: 0,
          maxRotation: 0,
          callback: function (this: Scale, val: string | number) {
            if (typeof val !== 'number') return val;
            const label = this.getLabelForValue(val);
            return label.split('\n');
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        itemSort: tooltipLabelPriority
          ? (a: TooltipItem<'line'>, b: TooltipItem<'line'>) => {
              const la = tooltipLabelPriority[a.dataset.label ?? ''] ?? 50;
              const lb = tooltipLabelPriority[b.dataset.label ?? ''] ?? 50;
              return la - lb;
            }
          : undefined,
        callbacks: {
          title: (tooltipItems: TooltipItem<'line'>[]) => {
            const datasetIndex = tooltipItems[limit ? 1 : 0].datasetIndex;
            const dataIndex = tooltipItems[limit ? 1 : 0].dataIndex;

            const item = datasets[datasetIndex].data[dataIndex];
            return item.label;
          },
          label: (tooltipItem: TooltipItem<'line'>) => {
            let datasetLabel = tooltipItem.dataset.label;
            const formattedValue = tooltipItem.formattedValue;
            const unit = yAxisLabel ? ` ${yAxisLabel}` : '';

            const shouldAddForecastLabel =
              realDataEndIndex &&
              realDataEndIndex > 0 &&
              tooltipItem.dataIndex > realDataEndIndex &&
              datasetLabel !== limitLabel;

            if (shouldAddForecastLabel) {
              datasetLabel = datasetLabel + ' (forecast)';
            }

            const valueToDisplay = tooltipValueFormatter
              ? `${datasetLabel}: ${tooltipValueFormatter(
                  +(tooltipItem.raw as string),
                  yAxisLabel,
                )}`
              : `${datasetLabel}: ${formattedValue}${unit}`;

            return valueToDisplay;
          },
        },
      },
      // @ts-expect-error -- how to register plugin
      overLimitAreaPlugin: {
        limit,
        clipUntil: realDataEndIndex,
        overlimitBackgroundColor: BACKGROUND_RED_COLOR,
        clippedBackgroundColor: BEFORE_PROJECTED_RED_AREA_BACKGROUND_COLOR,
        mode: 'both',
      },
      // how to register plugin
      customPointsPlugin: {
        pointRadius: pointRadius,
      },
    },
  };

  if (!datasets.length) {
    return (
      <Box sx={{ height: '100%' }}>
        <EmptyLineOrBarChart
          as={Line}
          emptyChartText={t('common.noResults')}
          xAxisLabels={getDefaultAreaChartXaxisLabels(true)}
        />
      </Box>
    );
  }

  return (
    <Line
      data={data}
      options={options}
      plugins={[overLimitAreaPlugin, gridOnTopPlugin, customPointsPlugin]}
    />
  );
};
